/**
 * Unlocked Videos Service
 *
 * Domain layer service for the unlocked-videos feature. Reads the local
 * unlocked-content records from encrypted storage.
 *
 * Singleton pattern for consistent state and efficient resource usage —
 * mirrors UserMediaService and other domain service singletons.
 *
 * Flutter parity:
 * - lib/presentation/screens/content/unlocked_content_screen.dart:69-78
 *   (_getLocalUnlockedContent: read + sort by timestamp desc)
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { UnlockedContentStorage } from '@/domain/entities/secureStorage/secureStorageEntities';

export class UnlockedVideosService {
  private static instance: UnlockedVideosService;

  private constructor() { }

  public static getInstance(): UnlockedVideosService {
    if (!UnlockedVideosService.instance) {
      UnlockedVideosService.instance = new UnlockedVideosService();
    }
    return UnlockedVideosService.instance;
  }

  /**
   * Read all locally-stored unlocked-content records, sorted newest first.
   *
   * The records are produced by SaveUnlockContentToSecureStorageUseCase and
   * persisted under the SECURE_STORAGE_KEYS.UNLOCKED_CONTENT prefix.
   *
   * Mirrors Flutter's _getLocalUnlockedContent in
   * unlocked_content_screen.dart:69-78.
   */
  async listLocalUnlockedContent(): Promise<UnlockedContentStorage[]> {
    const storage = SecureStorageWebCrypto.getInstance();
    const items = await storage.getAllWithPrefix<UnlockedContentStorage>(
      SECURE_STORAGE_KEYS.UNLOCKED_CONTENT,
    );
    return [...items].sort((a, b) => b.timestamp - a.timestamp);
  }
}

// Export singleton instance for convenience
export const unlockedVideosService = UnlockedVideosService.getInstance();
